import { SectionTitle } from '../common/SectionTitle';
import { LoadingSkeleton } from '../common/LoadingSkeleton';
import { ErrorMessage } from '../common/ErrorMessage';
import { InjuredPersonCard } from './InjuredPersonCard';
import { PageNation } from './PageNation';
import { Search } from './Search';
import { TargetDate } from './TargetDate';
import { Incident } from 'core/models/jianList/incident';
import { itemsPerPage, useJianList } from './hooks';
import { formatDateTime } from '../common/converters';
import { DateRangeType } from 'react-tailwindcss-datepicker';

type JianOverviewProps = {
  /** ローディング中かどうか。 */
  isLoading: boolean;
  /** エラーが発生したかどうか。 */
  isError: boolean;
  /** 事案一覧のデータ。 */
  jianListData: Incident[] | undefined;
  /** 現在のページ。 */
  currentPageState: number;
  /** 現在のページを設定する関数。 */
  setCurrentPageState: (page: number) => void;
  /** 選択された日付。 */
  selectedDate: DateRangeType;
};

const JianOverview = ({
  isLoading,
  isError,
  jianListData,
  currentPageState,
  setCurrentPageState,
  selectedDate,
}: JianOverviewProps) => {
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (isError) {
    return <ErrorMessage />;
  }

  // データが存在しない場合の表示。
  if (!jianListData) {
    return <EmptyMessage />;
  }

  const startIndex = (currentPageState - 1) * itemsPerPage;
  const currentItems = jianListData.slice(
    startIndex,
    startIndex + itemsPerPage
  );
  const totalPages = Math.ceil(jianListData.length / itemsPerPage);

  return (
    <>
      <div className="flex flex-col gap-[32px]">
        {currentItems.length > 0 ? (
          currentItems.map((incident, index) => {
            return (
              <div
                className="flex flex-col gap-[8px]"
                key={incident.incidentId}
              >
                <div className="flex gap-[8px]">
                  <IncidentTitle text={`事案${startIndex + index + 1}`} />
                  <IncidentTitle text={incident.incidentNo ?? ''} />
                  <IncidentTitle
                    text={formatDateTime(incident.recognitionAt)}
                  />
                </div>
                {incident.patients.map(patient => (
                  <InjuredPersonCard
                    key={patient.patientId}
                    patient={patient}
                    emsTeamName={incident.emsTeamName}
                    telegramTargetAddress={incident.telegramTargetAddress}
                    incidentId={incident.incidentId}
                    selectedDate={selectedDate}
                  />
                ))}
              </div>
            );
          })
        ) : (
          <EmptyMessage />
        )}
      </div>
      <PageNation
        totalPages={totalPages}
        currentPageState={currentPageState}
        setCurrentPageState={setCurrentPageState}
      />
    </>
  );
};

/** 事案一覧のコンポーネント。 */
export const JianList = () => {
  const {
    jianListData,
    isLoading,
    isError,
    handleDateChange,
    handleSearch,
    searchValue,
    setSearchValue,
    selectedDate,
    currentPageState,
    setCurrentPageState,
  } = useJianList();

  return (
    <div className="flex flex-col gap-[48px] max-w-[840px] m-auto px-[40px]">
      <Search
        onSearch={handleSearch}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
      />
      <div className="flex justify-between">
        <SectionTitle text="事案・傷病者一覧" />
        <TargetDate
          onDateChange={handleDateChange}
          selectedDate={selectedDate}
        />
      </div>
      <JianOverview
        isLoading={isLoading}
        isError={isError}
        jianListData={jianListData}
        currentPageState={currentPageState}
        setCurrentPageState={setCurrentPageState}
        selectedDate={selectedDate}
      />
    </div>
  );
};

const EmptyMessage = () => {
  return (
    <p className="text-[16px] noto-sans-jp text-textPrimary">
      事案が見つかりませんでした。
    </p>
  );
};

const IncidentTitle = ({ text }: { text: string }) => {
  return <p className="text-[16px] noto-sans-jp-bold text-primary">{text}</p>;
};
